import React, { useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { Modal, Row, Col, Divider, Button, Form, Checkbox, Select, Spin } from "antd"; //removed 'Space' due to unused var
import { ExportOutlined } from "@ant-design/icons";
import CatchError from "./CatchError";
import useExportFieldOrder from "./useExportFieldOrder";

const { Option } = Select;

const ExportModal = (props) => {
    let { t, export_header, keyname, title, additional_options, export_option, data_url, data, headers, fieldHeadersOptional, filename, delimiter, style, txalert_token, customToken, enableExportOrder } = props;

    const { getSavedOrder, saveOrder } = useExportFieldOrder(keyname);

    const [state, setState] = useState({ export_header: export_header ?? false, export_filetype: 'xlsx' });
    const [exportFields, setExportFields] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState([]);
    const [customOptions, setCustomOptions] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isDownloadModalVisible, setIsDownloadModalVisible] = useState(false);
    const [downloadUrl, setDownloadUrl] = useState(null);

    const [form] = Form.useForm();

    const layout = {
        labelCol: { span: 7 },
        wrapperCol: { span: 17 },
    };

    const showModal = () => {
        formValInit();
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setCustomOptions([]);
        setExportFields([]);
        setSelectedOrder([]);
        setIsModalVisible(false);
    };

    const getDefaultCheckedKeys = () => headers
        .filter((header) => !(fieldHeadersOptional && fieldHeadersOptional.includes(header.key)))
        .map((header) => header.key);

    const formValInit = () => {
        const defaultCheckedKeys = getDefaultCheckedKeys();

        let initialOrder = defaultCheckedKeys;
        if (enableExportOrder) {
            const savedOrder = getSavedOrder();
            if (savedOrder && savedOrder.length) {
                const headerKeys = headers.map((header) => header.key);
                const stillAvailable = savedOrder.filter((key) => headerKeys.includes(key));
                const newlyAvailable = defaultCheckedKeys.filter((key) => !savedOrder.includes(key));
                initialOrder = [...stillAvailable, ...newlyAvailable];
            }
            setSelectedOrder(initialOrder);
        }

        if (fieldHeadersOptional && Array.isArray(fieldHeadersOptional)) {
            setExportFields(headers.map((header) => ({ ...header, checked: enableExportOrder ? initialOrder.includes(header.key) : !fieldHeadersOptional.includes(header.key) })));
        } else {
            setExportFields(headers.map((header) => ({ ...header, checked: enableExportOrder ? initialOrder.includes(header.key) : true })));
        }

        if (additional_options) {
            setCustomOptions(additional_options.map((option) => ({ ...option, checked: false })));
        }

        setState({ export_header: export_header ?? false, export_filetype: 'xlsx' });
        form.setFieldsValue({ filename, delimiter: delimiter ? delimiter : ",", export_header: export_header ?? false, export_filetype: 'xlsx' });
    };

    const formValChange = (chgV, allV) => {
        setState(allV);
    };

    const onExportFieldChange = (fieldIndex) => {
        setExportFields(exportFields.map((exportField) => (exportField.key === fieldIndex ? { ...exportField, checked: !exportField.checked } : exportField)));

        if (enableExportOrder) {
            setSelectedOrder(selectedOrder.includes(fieldIndex) ? selectedOrder.filter((key) => key !== fieldIndex) : [...selectedOrder, fieldIndex]);
        }
    };

    const onAdditionalOptionChange = (optionIndex) => {

        setCustomOptions(customOptions.map((customOption) => (customOption.key === optionIndex ? { ...customOption, checked: !customOption.checked } : customOption)));
    }

    const onCheckAllChange = () => {
        let all_changed = null;
        const checkingAll = getIndeterminate() || !getCheckAll();
        if (checkingAll) {
            all_changed = headers.map((header) => ({ ...header, checked: true }));
        } else {
            all_changed = headers.map((header) => ({ ...header, checked: false }));
        }
        setExportFields(all_changed);

        if (enableExportOrder) {
            if (checkingAll) {
                const headerKeys = headers.map((header) => header.key);
                setSelectedOrder([...selectedOrder, ...headerKeys.filter((key) => !selectedOrder.includes(key))]);
            } else {
                setSelectedOrder([]);
            }
        }
    };

    const getIndeterminate = () => {
        let length = exportFields.filter((exportField) => exportField.checked === true).length;
        return length !== 0 && length !== exportFields.length;
    };

    const getOrderNumber = (key) => selectedOrder.indexOf(key) + 1;

    const onResetOrder = () => {
        const defaultCheckedKeys = getDefaultCheckedKeys();
        setSelectedOrder(defaultCheckedKeys);
        setExportFields(headers.map((header) => ({ ...header, checked: defaultCheckedKeys.includes(header.key) })));
        saveOrder(defaultCheckedKeys);
    };

    const onDeselectAll = () => {
        setExportFields(headers.map((header) => ({ ...header, checked: false })));
        setSelectedOrder([]);
    };

    const getExportOptionsKeyVal = () => {
        let options = {};
        let obj = customOptions.map((option) => ({ ...options, [option.key]: option.checked }));
        return Object.assign({}, ...obj);
    };

    const getCheckAll = () => exportFields.filter((exportField) => exportField.checked === false).length === 0;

    const closeDownloadModal = () => {
        setIsDownloadModalVisible(false);
        setDownloadUrl(null);
        setIsModalVisible(false);
    };

    const onDownload = async () => {
        if (!exportFields.some((exportField) => exportField.checked)) {
            CatchError(t("tables.actions.export_fields_none_selected"));
            return;
        }

        const date_format = "YYYY-MM-DD";

        let postData = data;
        if (export_option === true) {
            postData = {
                ...postData,
                export_filetype: state.export_filetype,
            }
        }
        getExportOptionsKeyVal();
        let orderedExportFields = exportFields;
        if (enableExportOrder) {
            orderedExportFields = [
                ...selectedOrder.map((key) => exportFields.find((exportField) => exportField.key === key)).filter(Boolean),
                ...exportFields.filter((exportField) => !selectedOrder.includes(exportField.key)),
            ];
            saveOrder(selectedOrder);
        }
        postData = {
            ...postData,
            export_header: state.export_header,
            export_fields: orderedExportFields,
            export_options: getExportOptionsKeyVal(),
            date_from: data.startDate ? data.startDate.format(date_format) : null,
            date_to: data.endDate ? data.endDate.format(date_format) : null,
        }

        if (postData.startDate && postData.endDate && postData.page && postData.size) {
            delete postData.startDate;
            delete postData.endDate;
            delete postData.page;
            delete postData.size;
        }
        setIsDownloadModalVisible(true);
        setDownloadUrl(null);
        let headers;
        if(customToken && customToken.length > 0){
            headers = { Authorization: "Bearer " + customToken };
        }else{
            headers = { Authorization: "Bearer " + (txalert_token === true ?  Cookies.get("txalert_token") : Cookies.get("access_token")) };
        }
        axios({
            url: data_url,
            method: "POST",
            data: postData,
            headers: headers,
        }).then(({ data }) => {
            if (data.count === 0){
                CatchError("No data to export");
                closeDownloadModal();
            }else{
                const url = data.data;
                setDownloadUrl(url);
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("target", "_blank");
                link.setAttribute("download", "");
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
                setIsDownloadModalVisible(false); // close generating report popup
            }
        }).catch((error) => {
            const errMsg = error?.response?.data?.error_msg?.details || error?.message || error.toString();
            if(errMsg.toLowerCase().trim() === 'no data to export') {
                CatchError("No data to export");
            } else {
                CatchError(`An error occurred while generating the report:\n ${errMsg},\n if the problem persists, please contact support`);
            }
            closeDownloadModal();
        });
    };

    return (
        <>
            <Button key={`btn_${keyname}`} type="link" style={style ? {...style,...{zIndex:4}} : {zIndex:4}} disabled={data.length === 0} onClick={showModal}>
                <div className="d-flex align-items-center">
                    <ExportOutlined style={{ fontSize: "18px" }} /> <div style={{ marginLeft: "4px" }}>{title}</div>
                </div>
            </Button>
            <Modal
                key={`modal_${keyname}`}
                title={t('tables.actions.export_settings')}
                visible={isModalVisible}
                onCancel={handleCancel}
                width={680}
                footer={[
                    <Button key="back" onClick={handleCancel}>
                        {t('tables.actions.cancel')}
                    </Button>,
                    <Button key="export" type="primary" onClick={() => onDownload()}>
                        {title}
                    </Button>,
                ]}
            >
                <Form {...layout} form={form} onValuesChange={formValChange}>
                    {export_option === true ?
                        <Form.Item label="Export As" name="export_filetype" style={{ marginBottom: ".5rem" }}>
                            <Select
                                showArrow="true"
                                maxTagCount="responsive"
                                style={{ width: "20%" }}
                                placeholder={t("tables.fields.field")}>
                                <Option value="xlsx">xlsx</Option>
                                <Option value="csv">csv</Option>
                            </Select>
                        </Form.Item>
                        : null}
                    {additional_options ?
                        additional_options.map((option) => (
                            <Form.Item label={option.label} name={option.key} key={option.key} valuePropName="checked" style={{ marginBottom: ".5rem" }}>
                                <Checkbox
                                    checked={option.checked}
                                    defaultChecked={option.checked}
                                    onChange={() => onAdditionalOptionChange(option.key)} />
                            </Form.Item>
                        ))
                        : null}
                    <Form.Item label={t('tables.actions.export_headers')} name="export_header" valuePropName="checked" style={{ marginBottom: ".5rem" }}>
                        <Checkbox />
                    </Form.Item>
                    <Form.Item label={t('tables.actions.export_fields')} style={{ marginBottom: ".5rem" }}>
                        <Checkbox indeterminate={getIndeterminate()} checked={getCheckAll()} onChange={onCheckAllChange} style={{ marginTop: ".35rem" }}>
                            All
                        </Checkbox>
                        {enableExportOrder ? (
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: ".35rem" }}>
                                <div style={{ color: "rgba(0, 0, 0, 0.45)", fontSize: "12px" }}>
                                    {t("tables.actions.export_fields_order_hint")}
                                </div>
                                <div>
                                    <Button type="link" size="small" style={{ padding: 0, marginRight: 12, height: "auto" }} onClick={onDeselectAll}>
                                        {t("tables.actions.export_fields_deselect_all")}
                                    </Button>
                                    <Button type="link" size="small" style={{ padding: 0, height: "auto" }} onClick={onResetOrder}>
                                        {t("tables.actions.export_fields_reset_order")}
                                    </Button>
                                </div>
                            </div>
                        ) : null}
                        <Divider style={{ marginTop: ".5rem", marginBottom: ".5rem" }} />
                        <Row>
                            {exportFields
                                ? exportFields.map((exportField) => (
                                    <Col span={12} key={exportField.key}>
                                        <Checkbox
                                            checked={exportField.checked}
                                            defaultChecked={exportField.checked}
                                            onChange={() => onExportFieldChange(exportField.key)}
                                        >
                                            {enableExportOrder && exportField.checked ? (
                                                <span
                                                    style={{
                                                        display: "inline-block",
                                                        minWidth: 18,
                                                        height: 18,
                                                        lineHeight: "18px",
                                                        borderRadius: "50%",
                                                        background: "#1890ff",
                                                        color: "#fff",
                                                        fontSize: 11,
                                                        textAlign: "center",
                                                        marginRight: 6,
                                                    }}
                                                >
                                                    {getOrderNumber(exportField.key)}
                                                </span>
                                            ) : null}
                                            {exportField.label}
                                        </Checkbox>
                                    </Col>
                                ))
                                : null}
                        </Row>
                    </Form.Item>
                </Form>
            </Modal>
            <Modal
                key={`download_${keyname}`}
                title="Generating Report"
                visible={isDownloadModalVisible}
                width={680}
                onCancel={closeDownloadModal}
                footer={[
                    <Button key="back" onClick={closeDownloadModal}>
                        {t('tables.actions.cancel')}
                    </Button>
                ]}
            >
                <div id="abcd"></div>
                {!downloadUrl ?
                    <div style={{ width: '100%', padding: '1rem', flexDirection: 'column' }} align="center" className="d-flex justify-content-center">
                        <Spin size="large"></Spin>
                        <div className="d-flex justify-content-center" style={{ marginTop: '1rem' }}>
                            <div>Please wait while we generate your report</div>
                        </div>
                    </div>
                    :
                    <div>
                        <div className="d-flex justify-content-center">
                            If your download doesn't start automatically, <a href={downloadUrl} target="_blank" rel="noopener noreferrer" style={{ paddingLeft: '.25rem' }}> Click here</a>
                        </div>
                    </div>
                }
            </Modal>
        </>
    );
};

export default ExportModal;
